import { AskAIButtonProps } from "@/components/static-dashboard-components/charts-components/ChartTopBar";
import { StarIcon } from "@/components/utils/icons/StarIcon";
import { Colors } from "@/enums/Colors";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, IconButton, IconButtonProps, styled, Typography } from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";

interface Props {
  collapseContent: ReactNode;
  onClickStarIcon?: () => void;
  title: string;
  value: string;
  colorValue?: string;
  disableExpand?: boolean;
  selected: boolean;
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

export const CollapseChart = ({
  collapseContent,
  title,
  onClickStarIcon,
  disableExpand,
  value,
  colorValue,
  selected,
  disableAskAIButton,
}: Props & AskAIButtonProps) => {
  const collapseRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (collapseRef.current && !collapseRef.current.contains(event.target as Node)) {
      setExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Container ref={collapseRef} className="main-container">
      <Box className="content-container">
        <Box className="title-container">
          <Typography className="title">{title}</Typography>
          {onClickStarIcon && !disableAskAIButton && <StarIcon onClick={onClickStarIcon} />}
        </Box>

        <Box className="value-container">
          <Typography sx={{ color: colorValue ?? Colors.BLACK_151517 }} className="chart-value">
            {value}
          </Typography>

          {!disableExpand && (
            <ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
              <ExpandMoreIcon />
            </ExpandMore>
          )}
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box className={`collapse-content-container ${selected ? "selected" : ""}`}>{collapseContent}</Box>
      </Collapse>
    </Container>
  );
};

const Container = styled(Box)(() => ({
  "& .content-container": {
    padding: "1.5rem",
    paddingBottom: "0",
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem",
  },
  "&.main-container": {
    minHeight: "100%",
    borderRadius: "1.5rem",
    rowGap: "0.5rem",
    backgroundColor: "white",
    zIndex: 1,
    position: "relative",
  },
  "& .title-container": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& .title": {
      color: Colors.LIGHT_GRAY_8A8E93,
      fontWeight: 600,
    },
  },
  "& .value-container": {
    display: "flex",
    "& .chart-value": {
      fontSize: "1.375rem",
      fontWeight: 500,
      textAlign: "justify",
    },
  },
  "& .collapse-content-container": {
    padding: "1.5rem",
    paddingTop: "1rem",
    boxShadow: "0px 4px 4px -2px rgba(0, 0, 0, 0.2)",
    borderBottomLeftRadius: "1.5rem",
    borderBottomRightRadius: "1.5rem",
    "&.selected": {
      boxShadow: ` 0 2px 0 ${Colors.LAZO_PURPLE}, 2px 0 0 ${Colors.LAZO_PURPLE}, -2px 0 0 ${Colors.LAZO_PURPLE}`,
    },
  },
}));

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));
